// Single source of truth for per-route SEO metadata.
//
// Consumed by:
//  - src/components/Seo.jsx (runtime <head> for the hydrated SPA)
//  - scripts/seo/build-seo.mjs (static per-route HTML + sitemap at build time)
//
// Keep every entry's `path` in sync with a real route in App.jsx. The sitemap
// and prerender only emit entries listed here.

import { site } from './site.js';
import { faqs, siteFaq } from './faqs.js';
import { posts } from './blog.js';
import {
  organizationNode,
  webSiteNode,
  softwareApplicationNode,
  breadcrumbNode,
  faqNode,
  productNode,
  localBusinessNode,
  articleNode,
  graph,
} from '../utils/schema.js';

const abs = (p) => `${site.url}${p}`;
const home = { name: 'Home', path: '/' };

// Concise helper: breadcrumb (Home > name) for a top-level page.
const crumb = (name, path) => breadcrumbNode([home, { name, path }]);

// Ordered list. `priority`/`changefreq` feed the sitemap; `noindex` excludes
// a page from both the sitemap and search engines.
export const routes = [
  {
    path: '/',
    title: null, // null => uses "Limosys LLC — <tagline>"
    description: site.description,
    image: '/assets/images/imageedit-21-8338150016-892x474.png',
    changefreq: 'weekly',
    priority: 1.0,
    jsonLd: graph([organizationNode(), webSiteNode(), softwareApplicationNode()]),
  },
  {
    path: '/platform',
    title: 'Limousine & Ground Transportation Software Platform',
    description:
      'The complete Limosys platform: dispatch, auto-dispatch & routing, fleet management, accounting & billing, driver and passenger apps, and website reservations — one integrated system.',
    changefreq: 'monthly',
    priority: 0.9,
    jsonLd: graph([
      breadcrumbNode([home, { name: 'Platform', path: '/platform' }]),
      softwareApplicationNode({
        name: 'Limosys Platform',
        url: abs('/platform'),
        description:
          'All-in-one limousine and ground transportation management software: reservations, dispatch, driver and passenger apps, billing, and reporting.',
      }),
    ]),
  },
  {
    path: '/custom',
    title: 'Custom Transportation Software Solutions',
    description:
      'No two operations are alike. Limosys tailors dispatch, apps, integrations, and add-ons to your exact workflow, with flexible licensing and a 25,000-car affiliate network.',
    changefreq: 'monthly',
    priority: 0.7,
    jsonLd: graph([breadcrumbNode([home, { name: 'Custom', path: '/custom' }])]),
  },
  {
    path: '/affiliates',
    title: 'Affiliate Network for Ground Transportation Operators',
    description:
      'Join a 25,000-car affiliate network. Farm out and receive trips, expand your coverage, and keep your fleet moving with the Limosys affiliate ecosystem.',
    changefreq: 'monthly',
    priority: 0.7,
    jsonLd: graph([breadcrumbNode([home, { name: 'Affiliates', path: '/affiliates' }])]),
  },
  {
    path: '/contact',
    title: 'Contact Limosys',
    description:
      "Talk to Limosys about your ground transportation operation. Call (855) LIMOSYS or email info@limosys.com — we're available 24/7 for questions and support.",
    changefreq: 'yearly',
    priority: 0.6,
    jsonLd: graph([breadcrumbNode([home, { name: 'Contact', path: '/contact' }])]),
  },
  {
    path: '/employment',
    title: 'Careers at Limosys',
    description:
      'Come work with us. Explore employment opportunities at Limosys, a leading provider of ground transportation software trusted for over 30 years.',
    changefreq: 'monthly',
    priority: 0.5,
    jsonLd: graph([breadcrumbNode([home, { name: 'Employment', path: '/employment' }])]),
  },
  {
    path: '/demo',
    title: 'Request a Demo',
    description:
      'Request a live Limosys demo. Tell us about your fleet and we will email you to schedule a walkthrough — no obligation.',
    changefreq: 'yearly',
    priority: 0.8,
    jsonLd: graph([breadcrumbNode([home, { name: 'Request a Demo', path: '/demo' }])]),
  },
  {
    path: '/get-started',
    title: 'Get Started with Limosys',
    description:
      'Request a personalized demo or create your Limosys account. Choose the path that fits — see the platform live, or get started and begin onboarding today.',
    changefreq: 'yearly',
    priority: 0.8,
    jsonLd: graph([breadcrumbNode([home, { name: 'Get Started', path: '/get-started' }])]),
  },
  // ---- Phase 2: money pages -------------------------------------------------
  {
    path: '/pricing',
    title: 'Limosys Pricing',
    description:
      'Custom Limosys pricing built around your fleet size and modules. No per-trip commissions. Request a quote for Starter, Growth, or Enterprise.',
    changefreq: 'monthly',
    priority: 0.9,
    jsonLd: graph([crumb('Pricing', '/pricing'), faqNode(faqs.pricing)]),
  },
  {
    path: '/dispatch-software',
    title: 'Limo Dispatch Software',
    description:
      'Limo dispatch software for livery and black car operators. Manual and auto-dispatch, GPS tracking, and fewer no-shows — trusted for over 30 years. Book a demo.',
    image: '/assets/images/imageedit-9-8512604910-700x700.png',
    changefreq: 'monthly',
    priority: 0.9,
    jsonLd: graph([
      crumb('Dispatch Software', '/dispatch-software'),
      productNode({
        name: 'Limosys Limo Dispatch Software',
        description: 'Limo dispatch software with manual and automated dispatch for livery and black car operators.',
        url: abs('/dispatch-software'),
        image: '/assets/images/imageedit-9-8512604910-700x700.png',
      }),
      faqNode(faqs.dispatch),
    ]),
  },
  {
    path: '/limo-reservation-software',
    title: 'Limo Reservation & Booking Software',
    description:
      'Take reservations directly from your website and branded app. Instant quotes, flight tracking, stored payments, and every booking synced to dispatch in real time.',
    image: '/assets/images/imageedit-15-2730346892-700x700.png',
    changefreq: 'monthly',
    priority: 0.85,
    jsonLd: graph([
      crumb('Reservation Software', '/limo-reservation-software'),
      productNode({
        name: 'Limosys Reservation & Booking Software',
        description: 'Online booking and reservation management for ground transportation operators.',
        url: abs('/limo-reservation-software'),
        image: '/assets/images/imageedit-15-2730346892-700x700.png',
      }),
      faqNode(faqs.reservation),
    ]),
  },
  {
    path: '/driver-app',
    title: 'Chauffeur & Driver App',
    description:
      'A branded driver app for chauffeurs: instant trip details, real-time GPS, status updates, in-vehicle payments, and e-signatures. Available on iOS and Android.',
    image: '/assets/images/drivers-app-iphone-x-400x761.png',
    changefreq: 'monthly',
    priority: 0.85,
    jsonLd: graph([
      crumb('Driver App', '/driver-app'),
      productNode({
        name: 'Limosys Driver App',
        description: 'Branded chauffeur app with GPS tracking, trip status, and in-vehicle payments.',
        url: abs('/driver-app'),
        image: '/assets/images/drivers-app-iphone-x-400x761.png',
      }),
      faqNode(faqs.driverApp),
    ]),
  },
  {
    path: '/passenger-app',
    title: 'Branded Passenger Booking App',
    description:
      'A white-label passenger app under your brand. Customers book, schedule, preview rates, track their vehicle live, and manage trips — the rideshare experience, your brand.',
    image: '/assets/images/3-notification-screen-for-passengers-474x843.png',
    changefreq: 'monthly',
    priority: 0.85,
    jsonLd: graph([
      crumb('Passenger App', '/passenger-app'),
      productNode({
        name: 'Limosys Passenger App',
        description: 'White-label passenger booking app for limousine and black car operators.',
        url: abs('/passenger-app'),
        image: '/assets/images/3-notification-screen-for-passengers-474x843.png',
      }),
      faqNode(faqs.passengerApp),
    ]),
  },
  {
    path: '/accounting-billing',
    title: 'Limo Accounting & Billing Software',
    description:
      'Run the full billing cycle: quotes, invoices, integrated credit card processing, payroll disbursements, expenses, and reporting — tied to every trip you dispatch.',
    changefreq: 'monthly',
    priority: 0.8,
    jsonLd: graph([
      crumb('Accounting & Billing', '/accounting-billing'),
      productNode({
        name: 'Limosys Accounting & Billing',
        description: 'Invoicing, payments, payroll, and reporting for ground transportation businesses.',
        url: abs('/accounting-billing'),
      }),
      faqNode(faqs.accounting),
    ]),
  },
  {
    path: '/ground-transportation-software',
    title: 'Ground Transportation Software',
    description:
      'All-in-one ground transportation software for livery, black car, limo, shuttle, and chauffeured operators — reservations, dispatch, apps, billing, and reporting.',
    changefreq: 'monthly',
    priority: 0.9,
    jsonLd: graph([
      crumb('Ground Transportation Software', '/ground-transportation-software'),
      softwareApplicationNode({
        name: 'Limosys Ground Transportation Software',
        url: abs('/ground-transportation-software'),
      }),
      faqNode(faqs.groundTransportation),
    ]),
  },
  {
    path: '/limosys-vs-limo-anywhere',
    title: 'Limosys vs Limo Anywhere',
    description:
      'Compare Limosys and Limo Anywhere for dispatch, branded apps, pricing, affiliate network, and support. See why operators choose Limosys. Request a demo.',
    changefreq: 'monthly',
    priority: 0.8,
    jsonLd: graph([crumb('Limosys vs Limo Anywhere', '/limosys-vs-limo-anywhere'), faqNode(faqs.limoAnywhere)]),
  },
  {
    path: '/limo-anywhere-alternative',
    title: 'The Limo Anywhere Alternative',
    description:
      'Looking for a Limo Anywhere alternative? Limosys offers full dispatch, branded driver and passenger apps, transparent pricing, and 30+ years of support. Book a demo.',
    changefreq: 'monthly',
    priority: 0.8,
    jsonLd: graph([crumb('Limo Anywhere Alternative', '/limo-anywhere-alternative'), faqNode(faqs.limoAnywhere)]),
  },
  {
    path: '/limosys-vs-moovs',
    title: 'Limosys vs Moovs',
    description:
      'Compare Limosys and Moovs for limo and black car operations: dispatch depth, branded apps, affiliate network, pricing, and support. See the difference.',
    changefreq: 'monthly',
    priority: 0.75,
    jsonLd: graph([crumb('Limosys vs Moovs', '/limosys-vs-moovs'), faqNode(faqs.moovs)]),
  },
  {
    path: '/privacy',
    title: 'Privacy Policy',
    description: 'How Limosys LLC collects, uses, and protects your information.',
    changefreq: 'yearly',
    priority: 0.2,
    jsonLd: graph([crumb('Privacy Policy', '/privacy')]),
  },
  {
    path: '/terms',
    title: 'Terms of Service',
    description: 'The terms governing use of the Limosys website and services.',
    changefreq: 'yearly',
    priority: 0.2,
    jsonLd: graph([crumb('Terms of Service', '/terms')]),
  },
  // ---- Phase 3: use-case cluster -------------------------------------------
  {
    path: '/black-car',
    title: 'Black Car & Livery Software',
    description:
      'Software for premium black car and livery operators: branded booking, reliable auto-dispatch with VIP override, real-time GPS, corporate accounts, and clean invoicing.',
    changefreq: 'monthly',
    priority: 0.8,
    jsonLd: graph([crumb('Black Car & Livery', '/black-car'), faqNode(faqs.blackCar)]),
  },
  {
    path: '/airport-transportation',
    title: 'Airport Transportation Software',
    description:
      'Flight-aware bookings, meet-and-greet workflows, and real-time tracking so every airport pick-up and drop-off runs on time. Software for airport transfer operators.',
    changefreq: 'monthly',
    priority: 0.8,
    jsonLd: graph([crumb('Airport Transportation', '/airport-transportation'), faqNode(faqs.airport)]),
  },
  {
    path: '/corporate-transportation',
    title: 'Corporate Transportation Software',
    description:
      'Manage corporate accounts, executive travel, and recurring bookings with reliable dispatch, branded apps, consolidated invoicing, and detailed reporting.',
    changefreq: 'monthly',
    priority: 0.8,
    jsonLd: graph([crumb('Corporate Transportation', '/corporate-transportation'), faqNode(faqs.corporate)]),
  },
  {
    path: '/shuttle',
    title: 'Shuttle & Shared-Ride Software',
    description:
      'Schedule routes, dispatch efficiently, and keep passengers informed for hotel, corporate, event, and shared-ride shuttle services.',
    changefreq: 'monthly',
    priority: 0.75,
    jsonLd: graph([crumb('Shuttle Services', '/shuttle'), faqNode(faqs.shuttle)]),
  },
  {
    path: '/nemt',
    title: 'NEMT Software',
    description:
      'Non-emergency medical transportation software: scheduling, vehicle and capacity matching, trip documentation, and automated reminders that reduce no-shows.',
    changefreq: 'monthly',
    priority: 0.75,
    jsonLd: graph([crumb('NEMT', '/nemt'), faqNode(faqs.nemt)]),
  },
  {
    path: '/funeral',
    title: 'Funeral Transportation Software',
    description:
      'Coordinate vehicles, chauffeurs, and schedules for funeral and livery transportation with the reliability and discretion these services require.',
    changefreq: 'monthly',
    priority: 0.7,
    jsonLd: graph([crumb('Funeral Transportation', '/funeral'), faqNode(faqs.funeral)]),
  },

  // ---- Phase 3: content hub -------------------------------------------------
  {
    path: '/blog',
    title: 'Blog',
    description:
      'Practical guides on limo dispatch, booking, and running a ground transportation business — from a team with 30+ years in the industry.',
    changefreq: 'weekly',
    priority: 0.7,
    jsonLd: graph([crumb('Blog', '/blog')]),
  },
  {
    path: '/faq',
    title: 'Frequently Asked Questions',
    description:
      'Answers to common questions about Limosys — dispatch, driver and passenger apps, online booking, billing, pricing, and support.',
    changefreq: 'monthly',
    priority: 0.6,
    jsonLd: graph([crumb('FAQ', '/faq'), faqNode(siteFaq)]),
  },
  {
    path: '/glossary',
    title: 'Ground Transportation Software Glossary',
    description:
      'Plain-English definitions of limo, livery, and ground transportation software terms — from auto-dispatch to white-label passenger apps.',
    changefreq: 'monthly',
    priority: 0.5,
    jsonLd: graph([crumb('Glossary', '/glossary')]),
  },

  // ---- Phase 4: local pages -------------------------------------------------
  {
    path: '/limo-software-nyc',
    title: 'Limo Software NYC',
    description:
      'Limo, black car, and livery software for NYC operators. Auto-dispatch, JFK/LGA/EWR airport workflows, branded apps, and 24/7 local support. Request a demo.',
    changefreq: 'monthly',
    priority: 0.7,
    jsonLd: graph([
      crumb('Limo Software NYC', '/limo-software-nyc'),
      localBusinessNode({ areaServed: 'New York City' }),
      faqNode(faqs.local),
    ]),
  },
  {
    path: '/limo-software-new-york',
    title: 'Limousine Software New York',
    description:
      'Limousine software for operators across New York State — dispatch, reservations, branded driver and passenger apps, billing, and 24/7 support.',
    changefreq: 'monthly',
    priority: 0.65,
    jsonLd: graph([
      crumb('Limo Software New York', '/limo-software-new-york'),
      localBusinessNode({ areaServed: 'New York' }),
      faqNode(faqs.local),
    ]),
  },
  {
    path: '/limo-software-new-jersey',
    title: 'Limo Software New Jersey',
    description:
      'Ground transportation software for New Jersey operators, from a NJ-based company. Dispatch, branded booking, billing, and local 24/7 support. Request a demo.',
    changefreq: 'monthly',
    priority: 0.65,
    jsonLd: graph([
      crumb('Limo Software New Jersey', '/limo-software-new-jersey'),
      localBusinessNode({ areaServed: 'New Jersey' }),
      faqNode(faqs.local),
    ]),
  },

  // ---- Phase 3: blog posts (generated from blog.js) -------------------------
  ...posts.map((p) => ({
    path: `/blog/${p.slug}`,
    title: p.title,
    description: p.description,
    ogType: 'article',
    changefreq: 'yearly',
    priority: 0.6,
    jsonLd: graph([
      breadcrumbNode([home, { name: 'Blog', path: '/blog' }, { name: p.title, path: `/blog/${p.slug}` }]),
      articleNode({
        title: p.title,
        description: p.description,
        url: abs(`/blog/${p.slug}`),
        datePublished: p.date,
      }),
    ]),
  })),

  {
    path: '/404',
    title: 'Page Not Found',
    description: 'The page you are looking for could not be found.',
    noindex: true,
  },
];

const byPath = new Map(routes.map((r) => [r.path, r]));

/** Look up SEO metadata for a route path (exact match). */
export function getRouteMeta(path) {
  return byPath.get(path) || null;
}
